import { useEffect, useState } from "react"
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router";
import { useAuthenticationDataContext } from "../../Helpers/AuthenticationDataProvider";
import useFetchWrapper, { HttpResponse } from "../../Helpers/useFetchWrapper";
import { Comment, CommentsResponse, FormField, FormModel, Participant, ResourceRespose, Role, Status, User, UsersResponse } from "./ResourceDetail.model";

import { formattedMentoringScopes } from "../../Helpers/StaticData";
import useObjectHelper from "../../Helpers/useObjectHelper";
import { useAuthorizationDataContext } from "../../Helpers/AuthorizationDataProvider";

export const pageSize = 5;

export const useResourceDetail = () => {
	const [commentsPagination, setCommentsPagination] = useState({page: 1, size: pageSize})
	const [editCommentId, setEditCommentId] = useState('');
	const [loading, setLoading] = useState(true);
	const [members, setMembers] = useState<User[]>([]);
	const [newComment, setNewComment] = useState<string>();
	const [participants, setParticipants] = useState<User[]>([]);
	const [resourceComments, setComments] = useState<Comment[]>();
	const [resource, setResource] = useState<ResourceRespose>();
	const [showEditForm, setShowEditForm] = useState(false);
	const [showDeletePopup, setShowDeletePopup] = useState(false);
	const [showCommentInput, setShowCommentInput] = useState(false);
	const [showLoadMoreButton, setShowLoadMoreButton] = useState(true);
	const [showMediaPopup, setShowMediaPopup] = useState(false);

	const { id } = useParams<{ id: string }>();
	const { userId, imageId, getFullName } = useAuthenticationDataContext();
	const { role, isAuthorized } = useAuthorizationDataContext();
	const { get, post, put, remove } = useFetchWrapper();
	const intl = useIntl();
	const { arrToOptionList } = useObjectHelper();
	const history = useHistory();

	const { control, formState: { errors }, handleSubmit, setValue } = useForm<FormModel>({
		defaultValues: {
			acceptsComments: resource?.acceptsComments,
			author: resource?.author.userId,
			description: resource?.description,
			researchFields: resource?.researchFields,
			title: resource?.title,
		}
	});

	const authorsOptionList = role !== Role.Admin ?
		[{ label: `${getFullName()} (${intl.formatMessage({id:"basics.me"})})`, value: userId }] :
		members?.filter(item => item.role === 'Mentor').map((member) => {
		return {
			label: `${member.firstName} ${member.lastName}`, value: member.userId
		}
	}).concat([{ label: `${getFullName()} (${intl.formatMessage({id:"basics.me"})})`, value: userId }]);

	const resourceInformationFields: FormField[] = [
		{
			id: 'author',
			label: intl.formatMessage({ id: 'resourcesLabels.author' }),
			layout: 'w100',
			options: {
				optionList: authorsOptionList
			},
			rules: { required: true },
			type: 'select',
		},
		{
			id: 'title',
			label: intl.formatMessage({ id: 'resourcesLabels.title' }),
			layout: 'w100',
			rules: { required: true },
			type: 'text',
		},
		{
			id: 'description',
			label: intl.formatMessage({ id: 'resourcesLabels.description' }),
			layout: 'w100',
			rules: { required: true },
			type: 'textArea',
		},
	];

	useEffect(() => {
		getResource();
	}, []);

	useEffect(() => {
		getComments();
	}, [commentsPagination.size]);

	useEffect(() => {
		if (showEditForm) {
			getMembersList();
		}
	}, [showEditForm]);

	const addComment = async () => {
		if (newComment && newComment?.replace(/\s/g, '')) {
			await post(`/resource/${id}/comment`, { content: newComment });
			getComments();
			setNewComment('');
			setShowCommentInput(false);
		}
	}

	const addParticipant = (participant: Participant) => {
		setMembers(members.filter((item) => item.userId !== participant.userId));
		setParticipants([
			...participants,
			participant,
		]);
	}

	const deleteComment = async (commentId: string) => {
		await remove(`/resource/${id}/comment/${commentId}`);
		getComments();
	}

	const deleteResource = async () => {
		await remove(`/resource/personal/${id}`);
		history.push(getBreadCrumbLink());
	}

	const editComment = async () => {
		await put(`/resource/${id}/comment/${editCommentId}`, { content: newComment });
		setEditCommentId('');
		getComments();
	}

	const isAllowedToEdit = () => {
		return isCreator() || isAdmin();
	}

	const isAllowedToEditComment = (comment: Comment) => {
		return isAdmin() || userId === comment.createdBy.userId;
	}

	const getBreadCrumbLink = () => {
		if (isAdmin()) {
			return {pathname: '/resource-list', state: { activeTab: resource?.status === Status.Published ? '1' : '2'}};
		}
		else return { pathname: '/resources', state: { activeTab: !isCreator() ? '1' : '2'}};
	}

	const getComments = async () => {
		const response: HttpResponse<CommentsResponse> = await get(`/resource/${id}/comment?page=${commentsPagination.page}&size=${commentsPagination.size}`);
		if (response.parsedBody) {
			const { comments, count } = response.parsedBody;
			setComments(comments);
			commentsPagination.size >= count ? setShowLoadMoreButton(false) : setShowLoadMoreButton(true);
		} 
	}

	const getMembersList = async () => {
		const response: HttpResponse<UsersResponse[]> = await get(`/user/all-types`);
		response.parsedBody && setMembers(response.parsedBody.map((item) => {
			const { firstName, lastName, imageId, isDeleted, userId, role } = item;
			return { firstName, imageUrl: imageId, isDeleted, lastName, role, userId};
		}));
	}

	const getResource = async () => {
		!loading && setLoading(true);

		const response: HttpResponse<ResourceRespose> = await get(`/resource/global/${id}`);

		if (response.parsedBody) {
			setResource(response.parsedBody);
			setDefaultValues(response.parsedBody);
			setParticipants(response.parsedBody.participants);
		}

		setLoading(false);
	}

	const isAdmin = () => {
		return role === 'Admin';
	}
	
	const isCreator = () => {
		return resource?.createdBy.userId === userId && role === 'Mentor';
	}

	const navigateTo = (path: string) => {
		history.push(path);
	}

	const onClickUser = (user: User) => {
		if (user.role !== 'Admin'  && !user.isDeleted && role !== "ApplicantMentee") {
			if (!isAdmin()) navigateTo(user.userId === userId ? '/my-profile' : `/members-list/${user.userId}`);
			else navigateTo(`/users/${user.userId}`);
		}
	}

	const onCancelForm = () => {
		resource && setDefaultValues(resource);
		setShowEditForm(false);
	}

	const onCommentsInputChange = (event: any) => {
		event.target && setNewComment(event.target.value);
	}

	const onDownload = async() => {
		resource?.status === Status.Published && setTimeout(async () => { await put(`/resource/${id}/download`, {}) }, 10);
	}

	const onLoadMoreComments = () => {
		setCommentsPagination({ ...commentsPagination, size: commentsPagination.size * 2 });
	}

	const onSetCommentEditId = (comment: Comment) => {
		setNewComment(comment.content);
		setShowCommentInput(false);
		setEditCommentId(comment.id);
	}

	const onSubmitForm = async (data: any) => {
		const submitData = { ...data, participants: participants.map(item => item.userId) };
		await put(`/resource/personal/${resource?.id}`, submitData);
		setShowEditForm(false);
		getResource();
	}

	const publishResource = async () => {
		await put(`/resource/admin/${id}`, { Approve: {} });
		getResource();
	}

	const setDefaultValues = (resource: ResourceRespose) => {
		setValue("acceptsComments", resource.acceptsComments);
		setValue("author", resource.author.userId);
		setValue("description", resource.description);
		setValue("researchFields", resource.researchFields);
		setValue("title", resource.title);
	}

	const toggle = (set: React.Dispatch<React.SetStateAction<any>>, value: boolean) => {
		set(!value);
	}

	const unpublishResource = async () => {
		await put(`/resource/admin/${id}`, { Reject: {} });
		getResource();
	}

	const removeParticipant = (participant: Participant) => {
		setParticipants(participants.filter((item) => item.userId !== participant.userId));
		setMembers([
			...members,
			participant,
		]);
	}

	const onMediaReady = (player: any) => {
		 player.on('waiting', () => {
      console.log('player is waiting');
    });
	}

	const canAddApplicantMentees = () => {
		return isAuthorized(['ViewApplicantMentees']) || isAdmin();
	}

	return {
		addComment,
		addParticipant,
		canAddApplicantMentees,
		control,
		deleteComment,
		deleteResource,
		editComment,
		getBreadCrumbLink,
		handleSubmit,
		isAdmin,
		isAllowedToEdit,
		isAllowedToEditComment,
		isCreator,
		onCancelForm,
		onClickUser,
		onCommentsInputChange,
		onDownload,
		onLoadMoreComments,
		onMediaReady,
		onSetCommentEditId,
		onSubmitForm,
		publishResource,
		removeParticipant,
		resourceInformationFields,
		setEditCommentId,
		state: {
			currentUserFullName: getFullName(),
			currentUserImageId: imageId,
			editCommentId,
			errors,
			loading,
			members,
			mentoringScopes: formattedMentoringScopes,
			newComment,
			participants,
			resource,
			resourceComments,
			showCommentInput,
			showDeletePopup,
			showEditForm,
			showLoadMoreButton,
			showMediaPopup
		},
		toggleCommentInput: () => { toggle(setShowCommentInput, showCommentInput); setNewComment(''); setEditCommentId('') },
		toggleDeletePopup: () => { toggle(setShowDeletePopup, showDeletePopup); },
		toggleEditForm: () => { toggle(setShowEditForm, showEditForm); },
		toggleMediaPopup: ()=> {toggle(setShowMediaPopup, showMediaPopup)},
		unpublishResource,
	}
}